/**
 * Report analyzer service using Gemini-2.0-Flash via serverless proxy
 */

import { TranscriptEntry } from "./store-transcript";
import { ReportData } from "./store-report";

// API endpoint - serverless function (key is server-side, not exposed)
const API_ENDPOINT = "/api/gemini";

export async function analyzeConversation(
    transcripts: TranscriptEntry[],
    persona: string,
    startTime: Date | null
): Promise<ReportData> {
    const formattedConversation = transcripts.map((t) => {
        const relativeTime = startTime
            ? formatTimestamp(t.timestamp.getTime() - startTime.getTime())
            : "00:00";
        const role = t.role === "ai" ? "Müşteri" : "Satıcı";
        return `${relativeTime} ${role}: ${t.text}`;
    }).join("\n");

    const prompt = `Sen bir satış eğitmeni ve analistsin. Aşağıdaki satış görüşmesini analiz et.

MÜŞTERİ PERSONA:
${persona}

KONUŞMA:
${formattedConversation}

Aşağıdaki JSON formatında SADECE JSON olarak yanıt ver:
{
  "overallScore": 0-100,
  "shortAnalysis": "2-3 cümlelik genel değerlendirme",
  "saleOutcome": {
    "saleMade": true/false,
    "explanation": "Kısa açıklama"
  },
  "personaAnalysis": "Müşteri karakterine uygun yaklaşım analizi",
  "positives": ["iyi yaptığı şey 1", "..."],
  "negatives": ["geliştirilmesi gereken 1", "..."],
  "conversationFeedback": [
    {"timestamp": "00:00", "role": "seller", "text": "mesaj", "feedback": "geri bildirim veya null"}
  ],
  "criticalMoments": [
    {"timestamp": "01:30", "type": "opportunity_gained|opportunity_missed", "description": "Ne oldu", "suggestion": "Öneri"}
  ],
  "detailedAnalysis": "En az 4-5 paragraf detaylı analiz"
}

KURALLAR:
- Sadece JSON döndür. conversationFeedback'te tüm mesajları kronolojik sırayla listele.
- Sadece satıcı mesajlarına feedback yaz, müşteri mesajlarında feedback null olsun.
- criticalMoments'ta en önemli 3-5 anı seç. detailedAnalysis somut örnekler içersin.
- Türkçe yaz.`;

    try {
        let result: string;

        // Try serverless function first (production)
        const response = await fetch(API_ENDPOINT, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ prompt }),
        });

        // If serverless fails (local dev), use direct API
        if (!response.ok || response.headers.get("content-type")?.includes("text/html")) {
            const apiKey = process.env.REACT_APP_GEMINI_API_KEY;
            if (!apiKey) {
                throw new Error("Gemini API key bulunamadı. .env dosyasını kontrol edin.");
            }

            const directResponse = await fetch(
                `https://generativelanguage.googleapis.com/v1beta/models/gemini-3.1-flash-lite-preview:generateContent?key=${apiKey}`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        contents: [{ parts: [{ text: prompt }] }],
                        generationConfig: {
                            temperature: 0.7,
                            maxOutputTokens: 8192,
                        }
                    }),
                }
            );

            if (!directResponse.ok) {
                const error = await directResponse.json();
                throw new Error(error.error?.message || "Gemini API hatası");
            }

            const data = await directResponse.json();
            result = data.candidates?.[0]?.content?.parts?.[0]?.text || "";
        } else {
            const data = await response.json();
            result = data.result;
        }

        // Temizle ve JSON parse et
        const cleanJson = (text: string) => {
            // Markdown kod bloklarını temizle
            let cleaned = text.replace(/```json\n?|\n?```/g, "").trim();
            // İlk { öncesini ve son } sonrasını temizle
            const firstBrace = cleaned.indexOf("{");
            const lastBrace = cleaned.lastIndexOf("}");
            if (firstBrace !== -1 && lastBrace !== -1) {
                cleaned = cleaned.substring(firstBrace, lastBrace + 1);
            }
            return cleaned;
        };

        try {
            const report: ReportData = JSON.parse(cleanJson(result));
            return report;
        } catch (e) {
            console.warn("İlk JSON parse denemesi başarısız, onarmaya çalışılıyor...", e);
            try {
                // Basit onarımlar:
                // 1. Satır sonlarındaki virgülleri temizle (trailing commas)
                // 2. Eksik tırnakları tamamla (basit regex ile zor ama denenebilir)
                // Bu örnekte sadece trailing comma ve bazı yaygın hataları regex ile temizlemeyi deniyoruz
                let fixed = cleanJson(result)
                    .replace(/,\s*}/g, "}") // obj sonundaki virgül
                    .replace(/,\s*]/g, "]"); // array sonundaki virgül

                const report: ReportData = JSON.parse(fixed);
                return report;
            } catch (e2) {
                console.error("JSON onarımı da başarısız oldu:", e2);
                throw new Error("JSON yanıtı işlenemedi: " + (e2 as Error).message);
            }
        }
    } catch (error: any) {
        console.error("Report analysis error:", error);
        throw new Error("Rapor analizi başarısız: " + error.message);
    }
}

function formatTimestamp(ms: number): string {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
}

